import apiClient from '../client';
import type { APIEntity } from '../APIEntity';
import type { Project, ProjectAssetMenu, ProjectMenuNode } from '../entities/project';
import type { Bookmark } from '../entities/bookmark';
import type { Workspace } from '../entities/workspace';
import type { RagIndex } from '../entities/rag-index';
import type { GitOrigin } from '../models';
import type { GitProvider } from '../services/git-providers';
import { isHubOnly } from '../utils/hub-runtime';
import { scopeFilterKey, scopeToQueryString, type ScopeFilter } from '../utils/scope-filter';
import { defineAsset, type LoadContext } from './definition';
import { LazyAsset } from './LazyAsset';

/**
 * Re-read `asset` whenever the backend says a STATUS fact changed (`status_changed_msg`): a CLI
 * installed, a login probed, a key stored, the hub signed in or out. One signal for every reader
 * of status and of funding layered on it, so no screen wires its own cross-cache invalidation.
 */
async function onStatusChanged(asset: LazyAsset, params: unknown): Promise<() => void> {
  if (isHubOnly()) return () => {};
  const { connectionManager } = await import('../websocket');
  const { lazyAssets } = await import('./registry');
  const handler = () => {
    // This subscription's own key: each cached key holds its own listener.
    void lazyAssets.invalidate(asset, params as never);
  };
  connectionManager.on('on_status_changed_msg', handler);
  // A reconnect is a missed-push window: the backend may have restarted (a fresh boot sweep, its
  // own push sent before this socket was back) or changed while the socket was down.
  connectionManager.on('on_reconnected', handler);
  return () => {
    connectionManager.off('on_status_changed_msg', handler);
    connectionManager.off('on_reconnected', handler);
  };
}

export interface AssetTypeVault {
  typeid: string; relPath: string; absPath: string; label: string; scope: string;
  project_id?: string | null; record_project_id?: string | null;
}
export interface AssetCatalog { types: { type_name: string; vaults?: AssetTypeVault[] }[] }
export interface IndexStatusPerType {
  type_name: string; last_indexed_at: string | null; entity_count: number; stale: boolean; orphan_count: number;
}
export interface IndexStatus {
  never_indexed: boolean; last_indexed_at: string | null; stale: boolean; default_types: string[];
  per_type: IndexStatusPerType[]; total_orphans: number;
}
export interface AssetStats { per_type: Record<string, number>; total: number }
export interface FavoriteRef { type: string; id: string }
export interface FavoriteSummary { name: string | null; subtitle: string | null }
export interface FavoriteSummaries { summaries: (FavoriteRef & FavoriteSummary)[] }
type NodeParams = { nodeTypeId?: { type: string; id: string } };
const localNode = { type: 'compute_node', id: '@local' };
type Scoped = { scope?: ScopeFilter } | undefined;
const scopeKey = (p: Scoped) => p?.scope ? scopeFilterKey(p.scope) : 'all';
const scopedPath = (path: string, p: Scoped) => {
  const qs = p?.scope ? scopeToQueryString(p.scope) : '';
  return qs ? `${path}?${qs}` : path;
};
export const EMPTY_INDEX_STATUS: IndexStatus = {
  never_indexed: false, last_indexed_at: null, stale: false, default_types: [], per_type: [], total_orphans: 0,
};
export const EMPTY_ASSET_STATS: AssetStats = { per_type: {}, total: 0 };

/** The query store owns entities and live membership; Query caches only their references. */
function entities<T extends APIEntity<T>>(type: string, desktopOnly = false) {
  const request = async (callback?: (rows: T[]) => void) => {
    const { QueryRequest } = await import('../FlowSync/query');
    return new QueryRequest({ type, query: null, scope: [], name: `lazy:${type}`, callback });
  };
  return defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext): Promise<T[]> => {
      if (desktopOnly && isHubOnly()) return [];
      const { dataManager } = await import('../APIEntity');
      const req = await request();
      if (!ctx.isCurrent()) throw new Error('SDK scope changed');
      return dataManager.query<T>(req, true);
    },
    subscribe: async (_: undefined, publish: (data: T[]) => void) => {
      if (desktopOnly && isHubOnly()) return () => {};
      const { dataManager } = await import('../APIEntity');
      const req = await request(rows => publish([...rows]));
      return dataManager.watchQuery<T>(req);
    },
  });
}

/** Each shared read is declared once. Dynamic imports keep SDK singleton initialization acyclic. */
export const assetDefinitions = {
  [LazyAsset.RuntimeInfo]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext) => {
      const { fetchDeferredInfo } = await import('../services/deferredInfo');
      return fetchDeferredInfo(ctx);
    },
  }),
  [LazyAsset.CloudStatus]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext) => (await import('../services/cloud_login')).cloudManager.fetchStatus(ctx.isCurrent),
  }),
  [LazyAsset.Capabilities]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext) => (await import('../capabilities')).capabilityManager.fetchSnapshot(ctx.isCurrent),
  }),
  [LazyAsset.CapabilitySummary]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext) => (await import('../capabilities')).capabilityManager.fetchSummary(ctx.isCurrent),
  }),
  [LazyAsset.AssetCatalog]: defineAsset({
    load: async (_: undefined): Promise<AssetCatalog> => isHubOnly() ? { types: [] } :
      (await apiClient.get<AssetCatalog>('/assets/types')) ?? { types: [] },
    subscribe: async (_: undefined) => {
      if (isHubOnly()) return () => {};
      const { subscribeToEntityOps } = await import('../FlowSync/entity-ops');
      const { lazyAssets } = await import('./registry');
      return subscribeToEntityOps('project', () => { void lazyAssets.invalidate(LazyAsset.AssetCatalog); });
    },
  }),
  [LazyAsset.IndexStatus]: defineAsset({
    key: scopeKey,
    load: async (p: Scoped): Promise<IndexStatus> => {
      if (isHubOnly()) return EMPTY_INDEX_STATUS;
      const raw = await apiClient.get<Partial<IndexStatus>>(scopedPath('/graph/compute_node/@local/fs-records/index-status', p));
      const per_type = (raw?.per_type ?? []).map((row) => ({
        type_name: row.type_name, last_indexed_at: row.last_indexed_at ?? null,
        entity_count: row.entity_count ?? 0, stale: row.stale ?? false, orphan_count: row.orphan_count ?? 0,
      }));
      return { ...EMPTY_INDEX_STATUS, ...raw, per_type,
        total_orphans: raw?.total_orphans ?? per_type.reduce((sum, row) => sum + row.orphan_count, 0) };
    },
  }),
  [LazyAsset.AssetStats]: defineAsset({
    key: scopeKey,
    load: async (p: Scoped): Promise<AssetStats> => {
      if (isHubOnly()) return EMPTY_ASSET_STATS;
      const raw = await apiClient.get<Partial<AssetStats>>(scopedPath('/graph/compute_node/@local/fs-records/asset-stats', p));
      const per_type = raw?.per_type ?? {};
      return { per_type, total: raw?.total ?? Object.values(per_type).reduce((sum, n) => sum + n, 0) };
    },
    subscribe: async (p: Scoped, _publish: (data: AssetStats) => void, initial: AssetStats) => {
      const { subscribeToEntityOps } = await import('../FlowSync/entity-ops');
      const { lazyAssets } = await import('./registry');
      return subscribeToEntityOps(Object.keys(initial.per_type), () => {
        void lazyAssets.invalidate(LazyAsset.AssetStats, p ?? {});
      });
    },
  }),
  // The project's Assets menu (per-type counts + dependency rows). `contextDirs` is in
  // the key so a changed dependency list refetches; a create or delete of a listed type
  // changes a count, so those ops invalidate it.
  [LazyAsset.ProjectAssetMenu]: defineAsset({
    load: async (p: { projectId: string; contextDirs: string }): Promise<ProjectAssetMenu | null> => {
      const { Project: ProjectEntity } = await import('../entities/project');
      return ProjectEntity.getAssetMenuById(p.projectId);
    },
    subscribe: async (p: { projectId: string; contextDirs: string }, _publish: unknown, initial: ProjectAssetMenu | null) => {
      const { subscribeToEntityOps } = await import('../FlowSync/entity-ops');
      const { lazyAssets } = await import('./registry');
      const types = new Set<string>();
      const visit = (node: ProjectMenuNode) => {
        for (const g of node.groups ?? []) types.add(g.type_name);
        for (const child of node.children ?? []) visit(child);
      };
      if (initial?.root) visit(initial.root);
      return subscribeToEntityOps([...types], () => { void lazyAssets.invalidate(LazyAsset.ProjectAssetMenu, p); }, { ops: ['create', 'delete'] });
    },
  }),
  [LazyAsset.Projects]: entities<Project>('project'),
  [LazyAsset.Workspaces]: entities<Workspace>('workspace', true),
  [LazyAsset.Bookmarks]: entities<Bookmark>('bookmark', true),
  [LazyAsset.RagIndexes]: entities<RagIndex>('rag_index', true),
  [LazyAsset.DiscoveredProjects]: defineAsset({
    staleTime: 120_000,
    load: async (p: { nodeId: string; workspace?: string }, ctx: LoadContext) => {
      const { listProjectsFromComputeNode } = await import('../entities/compute-node/system-profile');
      const result = await listProjectsFromComputeNode(p.nodeId, ctx.signal, p.workspace);
      if (ctx.isCurrent()) (await import('../stores/project-cleanup-store')).ingestCleanupSummary(result.cleanup);
      return result;
    },
  }),
  [LazyAsset.ProjectResources]: defineAsset({
    staleTime: 60_000,
    load: async (p: { nodeId: string; encodedName: string; includeSessions: boolean }, ctx: LoadContext) =>
      (await import('../entities/compute-node/system-profile')).scanProjectFromComputeNode(
        p.nodeId, p.encodedName, 100, p.includeSessions, ctx.signal),
  }),
  [LazyAsset.Skills]: defineAsset({
    load: async (p: { nodeId: string }) => (await import('../entities/compute-node/system-profile')).fetchAllSkillsFromComputeNode(p.nodeId),
  }),
  [LazyAsset.FavoriteSummaries]: defineAsset({
    staleTime: 15_000,
    key: (p: { refs: FavoriteRef[] }) => p.refs.map(r => `${r.type}:${r.id}`).sort(),
    load: async (p: { refs: FavoriteRef[] }): Promise<FavoriteSummaries> => p.refs.length ?
      (await apiClient.post<FavoriteSummaries>('/api/v1/favorites/summary', p)) ?? { summaries: [] } : { summaries: [] },
  }),
  [LazyAsset.Activities]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined) => isHubOnly() ? [] : (await import('../activity')).listActivities(null, true),
  }),
  [LazyAsset.IndexActivity]: defineAsset({
    staleTime: Infinity,
    load: async (_: undefined, ctx: LoadContext) => (await import('../services/system-tools-service')).systemTools.fetchActivityStatus(ctx.isCurrent),
  }),
  [LazyAsset.Connections]: defineAsset({
    key: (p: (NodeParams & { projectId?: string }) | undefined) => [p?.nodeTypeId ?? localNode, p?.projectId ?? ''],
    staleTime: 10_000,
    load: async (p: (NodeParams & { projectId?: string }) | undefined) => {
      const { ConnectionsService } = await import('../services/connections-service');
      return new ConnectionsService(p?.nodeTypeId ?? localNode).fetchList(p?.projectId);
    },
    subscribe: async (p) => onStatusChanged(LazyAsset.Connections, p),
  }),
  [LazyAsset.DeployProviders]: defineAsset({
    // The hub's list changes only when an operator reconfigures it; a minute is plenty fresh.
    staleTime: 60_000,
    load: async (_: undefined) => (await import('../entities/deployment')).Deployment.providers(),
  }),
  [LazyAsset.Status]: defineAsset({
    // WHAT is on this box (installed, login, account, keys, hub). Python owns every fact; the
    // backend pushes `status_changed_msg` when one changes, so this never polls.
    staleTime: Infinity,
    key: (p: NodeParams | undefined) => [p?.nodeTypeId ?? localNode],
    load: async (p: NodeParams | undefined) => {
      if (isHubOnly()) return null;
      const { StatusService } = await import('../services/status-service');
      return new StatusService(p?.nodeTypeId ?? localNode).fetch();
    },
    subscribe: async (p) => onStatusChanged(LazyAsset.Status, p),
  }),
  [LazyAsset.LlmFunding]: defineAsset({
    staleTime: 10_000,
    // The project is part of the KEY, not just the request: `resolved` is scoped to it, so a
    // cache keyed on the node alone would hand one project's funding verdict to another.
    key: (p: (NodeParams & { projectId?: string }) | undefined) => [p?.nodeTypeId ?? localNode, p?.projectId ?? ''],
    load: async (p: (NodeParams & { projectId?: string }) | undefined) => {
      const { LlmSourcesService } = await import('../services/llm-sources-service');
      return new LlmSourcesService(p?.nodeTypeId ?? localNode).fetchStatus(p?.projectId);
    },
    // Funding is layered on status: when a status fact changes, the verdicts may too.
    subscribe: async (p) => onStatusChanged(LazyAsset.LlmFunding, p),
  }),
  [LazyAsset.GitRepos]: defineAsset({
    load: async (p: { provider: GitProvider }) => (await import('../services/git-providers')).fetchRepos(p.provider),
  }),
  [LazyAsset.GitBranches]: defineAsset({
    // Key on exactly what is SENT, not on the whole params object: the same repo
    // reaches this hook as a full `RepoSummary` (browsed) and as a synthesized
    // `{git_origin}` (pasted URL). Keyed by identity those are two cache entries
    // for one request, and any unrelated `RepoSummary` churn (a new `pushed_at`
    // after a repo-list refresh) would silently drop the branches.
    key: (p: { git_origin: GitOrigin }) => [p.git_origin.provider, p.git_origin.owner, p.git_origin.name],
    load: async (p: { git_origin: GitOrigin }) => (await import('../services/git-providers')).fetchBranches(p),
  }),
  [LazyAsset.GitOrgs]: defineAsset({
    load: async (p: { provider: GitProvider }) => (await import('../services/git-providers')).fetchOrgs(p.provider),
  }),
  [LazyAsset.GitOrgRepos]: defineAsset({
    key: (p: { provider: GitProvider; owner: string }) => [p.provider, p.owner.toLowerCase()],
    load: async (p: { provider: GitProvider; owner: string }) =>
      (await import('../services/git-providers')).fetchOrgRepos(p.provider, p.owner),
  }),
  [LazyAsset.GitInvitations]: defineAsset({
    load: async (p: { provider: GitProvider }) => (await import('../services/git-providers')).fetchInvitations(p.provider),
  }),
};

export type AssetParams<A extends LazyAsset> = Parameters<(typeof assetDefinitions)[A]['load']>[0];
export type AssetData<A extends LazyAsset> = Awaited<ReturnType<(typeof assetDefinitions)[A]['load']>>;
